/* eslint-disable */
import {
  CheckCircleFilled,
  CloseCircleFilled,
  EyeOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import {
  Button,
  Space,
  Spin,
  Tooltip,
  Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import { FilterDataProps, FilterValueProps, mappingFilterFields, mappingParamsFilter, mappingQueryParamsFilter } from "common/components/PageTable/filter";
import { getAllHistoryApproveSalaryService } from 'common/services/extends/approveSalary';
import { HistoryApproveSalaryData } from 'common/services/extends/approveSalary/types';
import { Level, ROUTE_PATHS } from "common/utils/constant";
import { formatDateTime } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';

export type TableDataTypes = {
} & HistoryApproveSalaryData;

const ApproveSalaryHistoryManagement: React.FC<ActiveRoles> = () => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();

  /* Selectors */
  const {
    defaultPageSize, advancedFilter
  } = useAppSelector((state) => state.system);
  const rolesUser = useAppSelector((state) => state.auth.roles);

  /* Search Params */
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => {
    const paramsObj = { ...Object.fromEntries(searchParams.entries()) };
    if (searchParams.has('page')) delete paramsObj.page;
    return paramsObj;
  }, [searchParams]);
  const pageParam = searchParams.get('page');

  /* States */
  const [currentPage, setCurrentPage] = useState(Number(pageParam || 1));
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [keyword, setKeyword] = useState('');
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));

  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  const queryKey = ['approve-salary-list', keyword, currentPage, currentView, selectedFilterList];

  /* Queries */
  const { data: dataList, isLoading } = useQuery(
    queryKey,
    () => getAllHistoryApproveSalaryService({
      ...(keyword && { keyword }),
      page: currentPage,
      limit: currentView,
      ...returnParamsFilter
    }),
  );

  /* Variables */
  const columns: ColumnsType<TableDataTypes> = [
    {
      title: t('approveSalary.code'),
      dataIndex: 'code',
      key: 'code',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.projectData?.code}
        </Typography.Text>
      ),
    },
    {
      title: t('approveSalary.programName'),
      dataIndex: 'programName',
      key: 'programName',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.projectData?.name}
        </Typography.Text>
      ),
    },
    {
      title: t('approveSalary.customerName'),
      dataIndex: 'customerName',
      key: 'customerName',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.projectData?.customerName}
        </Typography.Text>
      ),
    },
    {
      title: t('approveSalary.timeKeepingName'),
      dataIndex: 'timeKeepingName',
      key: 'timeKeepingName',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.logData?.name || ''}
        </Typography.Text>
      ),
    },
    {
      title: t('approveSalary.creatorTableSalary'),
      dataIndex: 'creatorTableSalary',
      key: 'creatorTableSalary',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.staffFullTimeData?.fullName}
        </Typography.Text>
      ),
    },
    {
      title: t('approveSalary.levelApprove'),
      dataIndex: 'levelApprove',
      key: 'levelApprove',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.logData?.levelApprove?.value ? t(Level[data.logData?.levelApprove?.value - 1].label) : ''}
        </Typography.Text>
      ),
    },
    {
      title: t('approveSalary.userApprove'),
      dataIndex: 'userApprove',
      key: 'userApprove',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.userApprove?.name || ''}
        </Typography.Text>
      ),
    },
    {
      title: t('approveSalary.statusApprove'),
      dataIndex: 'statusApprove',
      key: 'statusApprove',
      align: 'center',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {
            data.logData?.isReopen ? <Tooltip title={t('approveSalary.reopen')}><UndoOutlined style={{ color: 'orange', fontSize: '20px' }} /></Tooltip>
              : data.logData?.approve
                ? <Tooltip title={t('approveSalary.approved')}><CheckCircleFilled style={{ color: 'green', fontSize: '20px' }} /></Tooltip>
                : <Tooltip title={t('approveSalary.rejected')}><CloseCircleFilled style={{ color: 'red', fontSize: '20px' }} /></Tooltip>}
        </Typography.Text>
      ),
    },
    {
      title: t('approveSalary.timeApprove'),
      dataIndex: 'timeApprove',
      key: 'timeApprove',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatDateTime(data.logData?.createdAt)}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_: string, data: TableDataTypes) => (
        <Space>
          <Tooltip title={t('system.viewDetail')}>
            <Button
              icon={<EyeOutlined />}
              disabled={!getPermission(rolesUser, roles.PAYROLL_PERIOD_APPROVE_LOG)}
              onClick={() => navigate(`${ROUTE_PATHS.APPROVE_HISTORY_DETAIL}?payrollPeriodId=${data.logData?.payrollPeriodId}&approveLogId=${data.logData?.id}`)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSetCurrentPage = (page: number) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('page', page.toString());
    setCurrentPage(page);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if ((typeFilter === 'isNull' || typeFilter === 'isNotNull') && selectedFilterList.find((item) => item.key === data.key)) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, { ...data, index: counter }]);
    handleSetCurrentPage(1);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
    handleSetCurrentPage(1);
  };

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('payrollPeriodApproveLog', advancedFilter),
    [advancedFilter]
  );

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(Number(pageParam));
    } else {
      setCurrentPage(1);
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.historyApprove')}
      />
      <Spin spinning={isLoading}>
        <div className="t-mainlayout_wrapper">
          <PageTable
            handleSearch={setKeyword}
            isLoading={isLoading}
            noCheckbox
            tableProps={{
              initShowColumns: ['code', 'programName', 'customerName', 'timeKeepingName', 'creatorTableSalary', 'levelApprove', 'userApprove', 'statusApprove', 'timeApprove', 'action'],
              columns,
              pageData: dataList?.data || [],
              currentPage,
              pageSize: currentView,
              handleSetCurrentPage,
              handleSetCurrentView,
              total: dataList?.meta.total || 1,
              noBaseCol: true,
              noDeleteLanguage: true,
              filterFields
            }}
            filtersDataTable={{
              handleFilter,
              selectedFilterList,
              handleDeleteFilter,
            }}
          />
        </div>
      </Spin>
    </>
  );
};

export default ApproveSalaryHistoryManagement;
